/**
 * SecurityPage - Phase 5 Enhanced Security Dashboard
 * Comprehensive security monitoring and compliance management
 * 
 * @description Professional security interface featuring:
 * - Real-time security threat monitoring and analysis
 * - Compliance posture assessment and reporting
 * - Vulnerability scanning and remediation tracking
 * - Access control and identity management
 * - Security incident response and forensics
 * - Policy enforcement and audit trails
 */

import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Area,
  AreaChart
} from 'recharts';
import { useCloudProviderStore } from "@/stores/cloudProviderStore";
import { 
  getLocalProviderFilterOptions,
  getFilterSummary 
} from "@/utils/providerFiltering";
import {
  Shield,
  ShieldAlert,
  ShieldCheck,
  AlertTriangle,
  Eye,
  Lock,
  Unlock,
  Users,
  FileText,
  Scan,
  Bug,
  Activity,
  TrendingUp,
  TrendingDown,
  Clock,
  Download,
  Settings,
  MoreVertical
} from "lucide-react";

// Security data interfaces
interface SecurityMetric {
  name: string;
  score: number;
  status: 'good' | 'warning' | 'critical';
  trend: 'up' | 'down' | 'stable';
  change: number;
}

interface Vulnerability {
  id: string;
  title: string;
  severity: 'low' | 'medium' | 'high' | 'critical';
  type: 'configuration' | 'software' | 'access' | 'network';
  resource: string;
  provider: 'aws' | 'gcp' | 'azure';
  status: 'open' | 'in-progress' | 'resolved';
  discovered: string;
  cve?: string;
  description: string;
}

interface SecurityEvent {
  id: string;
  type: 'access' | 'policy' | 'threat' | 'compliance';
  severity: 'low' | 'medium' | 'high' | 'critical';
  title: string;
  description: string;
  source: string;
  timestamp: string;
  status: 'active' | 'investigating' | 'resolved';
}

interface ComplianceFramework {
  name: string;
  score: number;
  requirements: number;
  passed: number;
  failed: number;
  status: 'compliant' | 'non-compliant' | 'partial';
}

// Mock data
const securityMetrics: SecurityMetric[] = [
  { name: 'Overall Security Score', score: 87, status: 'good', trend: 'up', change: 2.5 },
  { name: 'Vulnerability Score', score: 92, status: 'good', trend: 'up', change: 1.8 },
  { name: 'Compliance Score', score: 78, status: 'warning', trend: 'down', change: -3.2 },
  { name: 'Access Control Score', score: 94, status: 'good', trend: 'stable', change: 0.1 }
];

const vulnerabilities: Vulnerability[] = [
  {
    id: 'vuln-001',
    title: 'Unrestricted SSH Access',
    severity: 'high',
    type: 'access',
    resource: 'web-server-prod-01',
    provider: 'aws',
    status: 'open',
    discovered: '2024-01-20T08:30:00Z',
    description: 'Security group allows SSH access from 0.0.0.0/0'
  },
  {
    id: 'vuln-002',
    title: 'Outdated Database Version',
    severity: 'medium',
    type: 'software',
    resource: 'user-database-main',
    provider: 'aws',
    status: 'in-progress',
    discovered: '2024-01-19T14:15:00Z',
    cve: 'CVE-2023-1234',
    description: 'Database running outdated version with known vulnerabilities'
  },
  {
    id: 'vuln-003',
    title: 'Unencrypted Storage Bucket',
    severity: 'critical',
    type: 'configuration',
    resource: 'backup-storage',
    provider: 'gcp',
    status: 'open',
    discovered: '2024-01-18T10:20:00Z',
    description: 'Storage bucket not encrypted at rest'
  },
  {
    id: 'vuln-004',
    title: 'Weak Password Policy',
    severity: 'medium',
    type: 'access',
    resource: 'Azure AD',
    provider: 'azure',
    status: 'resolved',
    discovered: '2024-01-17T16:45:00Z',
    description: 'Password policy does not meet security requirements'
  }
];

const securityEvents: SecurityEvent[] = [
  {
    id: 'event-001',
    type: 'threat',
    severity: 'high',
    title: 'Suspicious Login Activity',
    description: 'Multiple failed login attempts from unknown IP address',
    source: 'web-server-prod-01',
    timestamp: '2024-01-20T09:45:00Z',
    status: 'investigating'
  },
  {
    id: 'event-002',
    type: 'access',
    severity: 'medium',
    title: 'Privileged Access Granted',
    description: 'Administrator access granted to new user account',
    source: 'IAM Service',
    timestamp: '2024-01-20T08:30:00Z',
    status: 'active'
  },
  {
    id: 'event-003',
    type: 'compliance',
    severity: 'low',
    title: 'Compliance Check Failed',
    description: 'Resource configuration does not meet SOC 2 requirements',
    source: 'analytics-worker',
    timestamp: '2024-01-20T07:15:00Z',
    status: 'resolved'
  }
];

const complianceFrameworks: ComplianceFramework[] = [
  { name: 'SOC 2', score: 85, requirements: 64, passed: 54, failed: 10, status: 'partial' },
  { name: 'ISO 27001', score: 92, requirements: 114, passed: 105, failed: 9, status: 'compliant' },
  { name: 'GDPR', score: 78, requirements: 32, passed: 25, failed: 7, status: 'partial' },
  { name: 'HIPAA', score: 96, requirements: 18, passed: 17, failed: 1, status: 'compliant' }
];

const severityColors = {
  low: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300',
  medium: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300',
  high: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-300',
  critical: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300'
};

const statusColors = {
  open: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300',
  'in-progress': 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300',
  resolved: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300',
  active: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-300',
  investigating: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300'
};

const COLORS = {
  primary: '#3b82f6',
  success: '#10b981',
  warning: '#f59e0b',
  danger: '#ef4444',
  info: '#8b5cf6'
};

// Generate mock chart data
const generateSecurityTrendData = () => {
  return Array.from({ length: 30 }, (_, i) => ({
    date: new Date(Date.now() - (29 - i) * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    score: 85 + Math.sin(i / 5) * 10 + Math.random() * 5,
    threats: Math.floor(Math.random() * 20),
    incidents: Math.floor(Math.random() * 8)
  }));
};

export default function SecurityPage() {
  // Global provider state
  const globalProvider = useCloudProviderStore(state => state.provider);
  
  const [securityTrendData] = useState(generateSecurityTrendData());
  const [providerFilter, setProviderFilter] = useState("all");
  const [selectedSeverity, setSelectedSeverity] = useState("all");

  const filteredVulnerabilities = vulnerabilities.filter(vuln => {
    // First filter by global provider
    if (vuln.provider !== globalProvider) return false;
    // Then apply local provider filter within global scope
    if (providerFilter !== "all" && vuln.provider !== providerFilter) return false;
    if (selectedSeverity !== "all" && vuln.severity !== selectedSeverity) return false;
    return true;
  });

  const getVulnerabilityStats = () => {
    const total = filteredVulnerabilities.length;
    const critical = filteredVulnerabilities.filter(v => v.severity === 'critical').length;
    const high = filteredVulnerabilities.filter(v => v.severity === 'high').length;
    const open = filteredVulnerabilities.filter(v => v.status === 'open').length;
    
    return { total, critical, high, open };
  };

  const vulnStats = getVulnerabilityStats();

  return (
    <div className="space-y-6">
      {/* Header Section */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-foreground mb-2">Security</h1>
          <p className="text-muted-foreground">
            Comprehensive security monitoring and compliance management
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm">
            <Scan className="w-4 h-4 mr-2" />
            Security Scan
          </Button>
          <Button variant="outline" size="sm">
            <Download className="w-4 h-4 mr-2" />
            Report
          </Button>
          <Button variant="outline" size="sm">
            <Settings className="w-4 h-4 mr-2" />
            Configure
          </Button>
        </div>
      </div>

      {/* Security Metrics Overview */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {securityMetrics.map((metric, index) => {
          const TrendIcon = metric.trend === 'up' ? TrendingUp : 
                           metric.trend === 'down' ? TrendingDown : 
                           Activity;
          const trendColor = metric.trend === 'up' ? 'text-green-600' : 
                           metric.trend === 'down' ? 'text-red-600' : 
                           'text-gray-600';
          
          return (
            <Card key={index}>
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">{metric.name}</p>
                    <p className="text-2xl font-bold text-foreground">{metric.score}</p>
                  </div>
                  <Shield className={`w-8 h-8 ${
                    metric.status === 'good' ? 'text-green-600' :
                    metric.status === 'warning' ? 'text-yellow-600' :
                    'text-red-600'
                  }`} />
                </div>
                <div className="space-y-2">
                  <Progress value={metric.score} className="h-2" />
                  <div className={`flex items-center text-xs ${trendColor}`}>
                    <TrendIcon className="w-3 h-3 mr-1" />
                    {metric.change > 0 ? '+' : ''}{metric.change}% from last month
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Security Dashboard Tabs */}
      <Tabs defaultValue="overview" className="space-y-6">
        <TabsList className="grid w-full grid-cols-5">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="vulnerabilities">Vulnerabilities</TabsTrigger>
          <TabsTrigger value="compliance">Compliance</TabsTrigger>
          <TabsTrigger value="events">Security Events</TabsTrigger>
          <TabsTrigger value="access">Access Control</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Security Score Trend */}
            <Card>
              <CardHeader>
                <CardTitle>Security Score Trend</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={securityTrendData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.3} />
                    <XAxis dataKey="date" stroke="#6b7280" />
                    <YAxis stroke="#6b7280" />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: '#1f2937', 
                        border: '1px solid #374151',
                        borderRadius: '8px'
                      }}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="score" 
                      stroke={COLORS.primary} 
                      strokeWidth={2}
                      name="Security Score"
                    />
                  </LineChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* Threats & Incidents */}
            <Card>
              <CardHeader>
                <CardTitle>Threats & Incidents</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <AreaChart data={securityTrendData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.3} />
                    <XAxis dataKey="date" stroke="#6b7280" />
                    <YAxis stroke="#6b7280" />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: '#1f2937', 
                        border: '1px solid #374151',
                        borderRadius: '8px'
                      }}
                    />
                    <Area 
                      type="monotone" 
                      dataKey="threats" 
                      stackId="1"
                      stroke={COLORS.warning} 
                      fill={COLORS.warning}
                      fillOpacity={0.6}
                      name="Threats Detected"
                    />
                    <Area 
                      type="monotone" 
                      dataKey="incidents" 
                      stackId="1"
                      stroke={COLORS.danger} 
                      fill={COLORS.danger}
                      fillOpacity={0.6}
                      name="Security Incidents"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>

          {/* Quick Security Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Open Vulnerabilities</p>
                    <p className="text-2xl font-bold text-red-600">{vulnStats.open}</p>
                  </div>
                  <Bug className="w-8 h-8 text-red-600" />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Security Events</p>
                    <p className="text-2xl font-bold text-orange-600">{securityEvents.length}</p>
                  </div>
                  <AlertTriangle className="w-8 h-8 text-orange-600" />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Compliance Frameworks</p>
                    <p className="text-2xl font-bold text-blue-600">{complianceFrameworks.length}</p>
                  </div>
                  <FileText className="w-8 h-8 text-blue-600" />
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Vulnerabilities Tab */}
        <TabsContent value="vulnerabilities" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span>Vulnerability Management</span>
                <div className="flex gap-2">
                  <div className="flex flex-col gap-1">
                    <div className="text-xs text-muted-foreground">
                      {getFilterSummary(globalProvider, providerFilter)}
                    </div>
                    <Select value={providerFilter} onValueChange={setProviderFilter}>
                      <SelectTrigger className="w-[150px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {getLocalProviderFilterOptions(globalProvider).map(option => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <Select value={selectedSeverity} onValueChange={setSelectedSeverity}>
                    <SelectTrigger className="w-[150px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Severities</SelectItem>
                      <SelectItem value="critical">Critical</SelectItem>
                      <SelectItem value="high">High</SelectItem>
                      <SelectItem value="medium">Medium</SelectItem>
                      <SelectItem value="low">Low</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Vulnerability</TableHead>
                    <TableHead>Severity</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Resource</TableHead>
                    <TableHead>Provider</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Discovered</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredVulnerabilities.map((vuln) => (
                    <TableRow key={vuln.id}>
                      <TableCell>
                        <div>
                          <p className="font-medium text-foreground">{vuln.title}</p>
                          <p className="text-sm text-muted-foreground">{vuln.description}</p>
                          {vuln.cve && (
                            <Badge variant="outline" className="mt-1 text-xs">
                              {vuln.cve}
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge className={severityColors[vuln.severity]}>
                          {vuln.severity}
                        </Badge>
                      </TableCell>
                      <TableCell className="capitalize">{vuln.type}</TableCell>
                      <TableCell className="font-medium">{vuln.resource}</TableCell>
                      <TableCell className="uppercase">{vuln.provider}</TableCell>
                      <TableCell>
                        <Badge className={statusColors[vuln.status]}>
                          {vuln.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm">
                        {new Date(vuln.discovered).toLocaleDateString()}
                      </TableCell>
                      <TableCell>
                        <Button variant="ghost" size="sm">
                          <MoreVertical className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Compliance Tab */}
        <TabsContent value="compliance" className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {complianceFrameworks.map((framework, index) => (
              <Card key={index}>
                <CardHeader>
                  <CardTitle className="flex items-center justify-between">
                    <span>{framework.name}</span>
                    <Badge className={
                      framework.status === 'compliant' ? 'bg-green-100 text-green-800' :
                      framework.status === 'partial' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-red-100 text-red-800'
                    }>
                      {framework.status}
                    </Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-sm font-medium">Compliance Score</span>
                      <span className="text-2xl font-bold">{framework.score}%</span>
                    </div>
                    <Progress value={framework.score} className="h-3" />
                  </div>
                  
                  <div className="grid grid-cols-3 gap-4 text-sm">
                    <div className="text-center">
                      <div className="text-2xl font-bold text-foreground">{framework.requirements}</div>
                      <div className="text-muted-foreground">Total</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-green-600">{framework.passed}</div>
                      <div className="text-muted-foreground">Passed</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-red-600">{framework.failed}</div>
                      <div className="text-muted-foreground">Failed</div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        {/* Security Events Tab */}
        <TabsContent value="events" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Security Events</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {securityEvents.map((event) => (
                  <div key={event.id} className="p-4 border rounded-lg">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <Badge className={severityColors[event.severity]}>
                            {event.severity}
                          </Badge>
                          <Badge variant="outline" className="capitalize">
                            {event.type}
                          </Badge>
                          <Badge className={statusColors[event.status]}>
                            {event.status}
                          </Badge>
                        </div>
                        <h4 className="font-medium text-foreground mb-1">{event.title}</h4>
                        <p className="text-sm text-muted-foreground mb-2">{event.description}</p>
                        <div className="flex items-center gap-4 text-xs text-muted-foreground">
                          <span>Source: {event.source}</span>
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {new Date(event.timestamp).toLocaleString()}
                          </span>
                        </div>
                      </div>
                      <Button variant="ghost" size="sm">
                        <Eye className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Access Control Tab */}
        <TabsContent value="access" className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Active Users</p>
                    <p className="text-2xl font-bold text-foreground">47</p>
                  </div>
                  <Users className="w-8 h-8 text-blue-600" />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Privileged Accounts</p>
                    <p className="text-2xl font-bold text-foreground">8</p>
                  </div>
                  <Lock className="w-8 h-8 text-orange-600" />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Failed Logins (24h)</p>
                    <p className="text-2xl font-bold text-foreground">23</p>
                  </div>
                  <Unlock className="w-8 h-8 text-red-600" />
                </div>
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Access Control Policies</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 border rounded-lg">
                  <div className="flex items-center gap-3">
                    <ShieldCheck className="w-5 h-5 text-green-600" />
                    <div>
                      <p className="font-medium">Multi-Factor Authentication</p>
                      <p className="text-sm text-muted-foreground">Required for all privileged accounts</p>
                    </div>
                  </div>
                  <Badge className="bg-green-100 text-green-800">Enabled</Badge>
                </div>

                <div className="flex items-center justify-between p-4 border rounded-lg">
                  <div className="flex items-center gap-3">
                    <Clock className="w-5 h-5 text-blue-600" />
                    <div>
                      <p className="font-medium">Session Timeout</p>
                      <p className="text-sm text-muted-foreground">Auto-logout after 30 minutes of inactivity</p>
                    </div>
                  </div>
                  <Badge className="bg-blue-100 text-blue-800">Active</Badge>
                </div>

                <div className="flex items-center justify-between p-4 border rounded-lg">
                  <div className="flex items-center gap-3">
                    <ShieldAlert className="w-5 h-5 text-yellow-600" />
                    <div>
                      <p className="font-medium">Password Policy</p>
                      <p className="text-sm text-muted-foreground">Complex passwords with regular rotation</p>
                    </div>
                  </div>
                  <Badge className="bg-yellow-100 text-yellow-800">Review Required</Badge>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}